import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { getQueryFn, apiRequest, queryClient } from "@/lib/queryClient";
import { PageHeader } from "@/components/shared/page-header";
import { DataTable, type ColumnDef } from "@/components/shared/data-table";
import { ContractForm } from "@/components/admin/commercial/contract-form";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Plus, ScrollText } from "lucide-react";
import { formatDate } from "@/lib/format";
import { useToast } from "@/hooks/use-toast";

interface UserSummary {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
}

interface ClientSummary {
  id: string;
  name: string;
}

interface ContractRow {
  id: string;
  name: string;
  contractType: string;
  renewalMode: string;
  status: string;
  startDate: string;
  endDate: string | null;
  client: ClientSummary;
  owner: UserSummary | null;
  accountManager: UserSummary | null;
  resolvedOwnerUserId: string | null;
}

function formatUser(user: UserSummary | null) {
  if (!user) return "Unassigned";
  return [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email;
}

export default function ContractsPage() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContract, setEditingContract] = useState<ContractRow | null>(null);

  const { data: contracts, isLoading } = useQuery<ContractRow[]>({
    queryKey: ["/api/contracts"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const createMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const response = await apiRequest("POST", "/api/contracts", data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/contracts"] });
      setDialogOpen(false);
      toast({ title: "Contract created" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to create contract",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Record<string, unknown>;
    }) => {
      const response = await apiRequest("PATCH", `/api/contracts/${id}`, data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/contracts"] });
      setDialogOpen(false);
      setEditingContract(null);
      toast({ title: "Contract updated" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to update contract",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const columns: ColumnDef<ContractRow>[] = [
    {
      key: "name",
      header: "Contract",
      cell: (row) => (
        <div className="flex flex-col">
          <span className="font-medium">{row.name}</span>
          <span className="text-xs text-muted-foreground">{row.client.name}</span>
        </div>
      ),
    },
    {
      key: "type",
      header: "Type",
      cell: (row) => row.contractType.replace(/_/g, " "),
    },
    {
      key: "owner",
      header: "Resolved owner",
      cell: (row) => formatUser(row.owner ?? row.accountManager),
    },
    {
      key: "startDate",
      header: "Start",
      cell: (row) => formatDate(row.startDate),
    },
    {
      key: "status",
      header: "Status",
      cell: (row) => row.status.replace(/_/g, " "),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Contracts"
        description="Commercial agreements, owners, and renewal settings"
        action={
          <Button
            onClick={() => {
              setEditingContract(null);
              setDialogOpen(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            New Contract
          </Button>
        }
      />

      <DataTable
        columns={columns}
        data={contracts}
        isLoading={isLoading}
        rowKey={(row) => row.id}
        onRowClick={(row) => navigate(`/contracts/${row.id}`)}
        emptyIcon={ScrollText}
        emptyTitle="No contracts yet"
        emptyDescription="Create the first commercial contract to start structuring budgets."
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingContract ? "Edit Contract" : "Create Contract"}
            </DialogTitle>
          </DialogHeader>
          <ContractForm
            defaultValues={
              editingContract
                ? {
                    clientId: editingContract.client.id,
                    name: editingContract.name,
                    contractType: editingContract.contractType as any,
                    renewalMode: editingContract.renewalMode as any,
                    status: editingContract.status as any,
                    startDate: editingContract.startDate.slice(0, 10),
                    endDate: editingContract.endDate?.slice(0, 10) ?? "",
                    contractOwnerUserId:
                      editingContract.owner?.id ?? "inherit",
                  }
                : undefined
            }
            onSubmit={(data) => {
              if (editingContract) {
                updateMutation.mutate({ id: editingContract.id, data });
                return;
              }
              createMutation.mutate(data);
            }}
            isPending={createMutation.isPending || updateMutation.isPending}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
